import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { loadStripe } from '@stripe/stripe-js';
import { useStripe, useElements, Elements, PaymentElement } from '@stripe/react-stripe-js';
import close from '../../assets/images/close.png';
import coinJar from '../../assets/images/coin-jar.png';
import styles from './DonateModal.module.css';
import { submitDonationApi } from '../../api/fundraisers.js';
import formatNumber from '../../utils/formatNumber.js';
import Button from '../Button.jsx';
import FormDonateMoneyInput from '../form/FormDonateMoneyInput.jsx';
import FormTipInput from "../form/FormTipInput";
import FormInput from '../form/FormInput.jsx';
import FormTextEditor from '../form/FormTextEditor.jsx';
import FormError from '../form/FormError.jsx';
import FormButton from '../form/FormButton.jsx';

const Form = (props) => {
	const { fundraiserId } = useParams();
	const stripe = useStripe();
	const stripeElements = useElements();
	const stripePaymentElementOptions = {
		fields: {
			billingDetails: {
				email: 'never',
				address: {
					country: 'never'
				}
			}
		}
	};
	const [formData, setFormData] = useState({
		amount: '',
		tipView: 'slider',
		tipAmount: 0,
		tipPercent: 10,
		email: '',
		displayName: '',
		comment: ''
	});
	const initialErrorObject = {
		amount: false,
		email: false,
		apiError: false
	};
	const [errorObject, setErrorObject] = useState(initialErrorObject);
	const inputsMessagesErrorObject = {
		amount: 'Not a valid amount',
		email: 'Required'
	};
	const [buttonText, setButtonText] = useState('Donate');
	const [buttonSubtext, setButtonSubtext] = useState('');
	const [submissionInProgress, setSubmissionInProgress] = useState(false);

	const calculateTip = (amount, tipPercent) => {
		amount = Math.round(amount * 100);
		return Math.ceil(amount * (tipPercent / 100));
	};

	const updateDonationAmount = (event) => {
		const isNoMoreThanTwoDecimals = event.target.value.match(/^(\d*\.{0,1}\d{0,2}$)/);
		if(isNoMoreThanTwoDecimals){
			let tip;
			if(formData.tipView === 'slider'){
				tip = calculateTip(event.target.value, formData.tipPercent);
			}
			else if(formData.tipView === 'input'){
				tip = formData.tipAmount * 100;
			}
			setFormData({...formData, amount: event.target.value, tipAmount: (tip / 100)});
			props.setDonateAmount(Math.round(event.target.value * 100));
			props.setTipAmount(tip);
		}
	};

	const updateTipAmount = (event) => {
		const isNoMoreThanTwoDecimals = event.target.value.match(/^(\d*\.{0,1}\d{0,2}$)/);
		if(isNoMoreThanTwoDecimals){
			setFormData({...formData, tipAmount: event.target.value});
			props.setTipAmount(Math.round(event.target.value * 100));
		}
	};


	const updateTipPercent = (tipPercent) => {
		const tip = calculateTip(formData.amount, tipPercent);
		setFormData({...formData, tipPercent: tipPercent, tipAmount: (tip / 100)});
		props.setTipAmount(tip);
	};

	useEffect(() => {
		let donateText = 'Donate';
		if(formData.amount > 0){
			donateText = 'Donate $' + formatNumber(formData.amount);
		}
		setButtonText(donateText)
		let tipText = '';
		let tip;
		if(formData.tipView === 'slider'){
			tip = calculateTip(formData.amount, formData.tipPercent) / 100
		}
		else if(formData.tipView === 'input'){
			tip = formData.tipAmount;
		}
		if(tip > 0){
			tipText = 'plus $' + formatNumber(tip) + ' extra';
		}
		setButtonSubtext(tipText);
	}, [formData.amount, formData.tipView, formData.tipAmount, formData.tipPercent]);

	const checkForEmptyInputs = () => {
		setErrorObject(initialErrorObject);
		const newErrorObject = structuredClone(initialErrorObject);
		let foundError = false;
		for(const key of Object.keys(formData)){
			if(key !== 'tipPercent' && key !== 'tipAmount' && key !== 'displayName' && key !== 'comment' && !formData[key]){
				newErrorObject[key] = inputsMessagesErrorObject[key];
				foundError = true;
			}
		}
		setErrorObject(newErrorObject);
		return foundError;
	};

	const submit = async(event) => {
		event.preventDefault();
		setErrorObject(initialErrorObject);
		setSubmissionInProgress(true);
		if(checkForEmptyInputs()){
			setSubmissionInProgress(false);
			return;
		}
		if(!stripe || !stripeElements){
			setErrorObject((prevState) => ({...prevState, apiError: 'Stripe loading error'}));
			setSubmissionInProgress(false);
			return;
		}
		const submitPaymentResult = await stripeElements.submit();
		if(submitPaymentResult.error){
			setErrorObject((prevState) => ({...prevState, apiError: 'Payment error'}));
			setSubmissionInProgress(false);
			return;
		}
		const { confirmationToken, error: createConfirmationTokenError } = await stripe.createConfirmationToken({
			elements: stripeElements,
			params: {
				payment_method_data: {
					billing_details: {
						email: formData.email,
						address: {
							country: 'US'
						}
					}
				}
			}
		});
		if(createConfirmationTokenError){
			setErrorObject((prevState) => ({...prevState, apiError: 'Stripe create confirmation token error'}));
			setSubmissionInProgress(false);
			return;
		}
		const lastFour = confirmationToken.payment_method_preview?.type === 'card' ?
						 confirmationToken.payment_method_preview?.card?.last4 : confirmationToken.payment_method_preview?.us_bank_account?.last4
		const submitDonationResponse = await submitDonationApi(fundraiserId, formData, confirmationToken.payment_method_preview?.us_bank_account?.financial_connections_account,
                                                               confirmationToken.id, lastFour);
		let body;
		try{
			body = await submitDonationResponse.json();
		}
		catch(e){}
		if(submitDonationResponse && submitDonationResponse.ok){
			props.setDonatedAmount(formData.amount);
			props.setTippedAmount(formData.tipAmount * 100);
			props.setDonated(true);
            if(props.bottomView === 'updates'){
                props.setBottomView(formData.comment ? 'comments' : 'updates');
            }
		}
		else{
			const error = body?.error ? body.error : 'Donation submission failed';
			setErrorObject((prevState) => ({...prevState, apiError: error}));
		}
		setSubmissionInProgress(false);
	};

	return (
		<form className={styles.form} onSubmit={submit}>
			<div className={styles.formInputs}>
				<FormDonateMoneyInput className={styles.amountInput} value={formData.amount} update={updateDonationAmount} error={errorObject.amount}/>
				<FormTipInput className={styles.tipSlider} view={formData.tipView} tipAmount={formData.tipAmount} tipPercent={formData.tipPercent}
							  setView={(view) => setFormData({...formData, tipView: view})} updateAmount={updateTipAmount} updatePercent={updateTipPercent}/>
				<PaymentElement options={stripePaymentElementOptions}/>
				<FormInput className={styles.input} label="Email address" type="email" placeholder="ie. johnsmith@gmail.com" value={formData.email}
						   update={(event) => setFormData({...formData, email: event.target.value})} error={errorObject.email} required/>
				<FormInput className={styles.input} label="Display name" type="text" placeholder="Optional" value={formData.displayName}
						   update={(event) => setFormData({...formData, displayName: event.target.value})}/>
				<FormTextEditor className={styles.textEditor} label="Comment" placeholder="500 characters max" maxlength={500} value={formData.comment}
								update={(event) => setFormData({...formData, comment: event.target.value})}/>
			</div>
			{
				errorObject.apiError && (
					<FormError error={errorObject.apiError}/>
				)
			}
			<div className={classNames(styles.buttons, {[styles.buttonsApiError]: errorObject.apiError})}>
				<FormButton className={styles.button} style={{marginLeft: 'auto'}} text={buttonText} subtext={buttonSubtext} disabled={submissionInProgress}/>
				<Button className={styles.button} text="Close" onClick={props.close}/>
			</div>
		</form>
	)
};

const DonateModal = (props) => {
	const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);
	const [stripeElementsOptions, setStripeElementsOptions] = useState({
		mode: 'payment',
		amount: 50,
		currency: 'usd',
		payment_method_options: {
			us_bank_account: {
				verification_method: 'instant',
                financial_connections: {
                    prefetch: ['balances'],
                    permissions: ['payment_method', 'balances'],
                }
			}
		},
        on_behalf_of: props.organization?.stripe_account_id,
		fonts: [{
			family: 'Gotham',
			src: 'url(https://giveristdev.com/Gotham-Book.otf)'
		}],
		appearance: {
			variables: {
				colorText: '#1F363D80',
				tabIconColor: '#40798C',
				tabIconHoverColor: '#40798C',
				tabIconSelectedColor: '#40798C',
				borderRadius: '500px'
			},
			rules: {
				'.Tab': {
					padding: '10px 20px',
					color: '#1F363D',
					fontSize: '16px',
					boxShadow: 'none',
				},
				'.Tab:hover': {
				  	color: '40798C'
				},
				'.TabLabel:hover': {
				  	color: '40798C'
				},
				'.Tab--selected, .Tab--selected:hover, .Tab--selected:focus': {
					color: '#40798C',
					backgroundColor: '#70A9A11A',
					border: '1px solid #40798C !important',
					outline: 'none',
					boxShadow: '0px 6px 22px 0px #4E666E4D'
				},
				'.MenuIcon:hover': {
					backgroundColor: 'white'
				},
				'.PickerItem': {
					fontSize: '12px',
					border: '1px solid #1F363D1A',
					boxShadow: 'none'
				},
				'.PickerItem:hover': {
					backgroundColor: 'white',
					border: '1px solid #1F363D1A',
				},
				'.PickerItem:focus-visible': {
					border: '1px solid #40798C',
					outline: 'none',
					boxShadow: '0 3px 12px 0 #40798C12'
				},
				'.Dropdown': {
					borderRadius: '10px'
				},
				'.TermsText': {
					padding: '0 10px'
				},
				'.Block': {
					padding: '18px 16px',
					borderRadius: '10px'
				},
				'.Label': {
					margin: '6px 0 6px 8px',
					color: '#1F363D80',
					fontFamily: 'Gotham',
					fontSize: '14px',
					fontWeight: 500
				},
				'.Input': {
					height: '16px',
					padding: '18px 20px',
					color: '#1F363D',
					borderRadisu: '500px',
					boxShadow: 'none'
				},
				'.Input::placeholder': {
					color: '#1F363D80'
				},
				'.Input:focus': {
					border: '1px solid #40798C',
					outline: 'none',
					boxShadow: '0 3px 12px 0 #40798C12'
				},
				'.Error': {
					marginTop: '8px',
					marginLeft: '10px'
				}
			}
		}
	});
	const [donateAmount, setDonateAmount] = useState(0);
	const [tipAmount, setTipAmount] = useState(1000);
	const [donated, setDonated] = useState(false);
	const [donatedAmount, setDonatedAmount] = useState(0);
	const [tippedAmount, setTippedAmount] = useState(0);
	const [tippedText, setTippedText] = useState('');

	useEffect(() => {
		if(donateAmount > 50){
			setStripeElementsOptions({...stripeElementsOptions, amount: donateAmount + tipAmount});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [donateAmount, tipAmount]);

	useEffect(() => {
		setTippedText(tippedAmount > 0 ? ' plus $' + formatNumber(tippedAmount / 100) + ' extra' : '');
	}, [tippedAmount]);

	return (
		<div className={styles.modal}>
			<div className={classNames(styles.container, {[styles.donated]: donated})}>
				<div className={styles.header}>
					Donate now
				</div>
				<img className={styles.close} src={close} alt="Close" onClick={props.close}/>
				{
					!donated ? (
						<Elements stripe={stripePromise} options={stripeElementsOptions}>
							<Form bottomView={props.bottomView} setDonateAmount={setDonateAmount} setTipAmount={setTipAmount} setDonated={setDonated}
								  setDonatedAmount={setDonatedAmount} setTippedAmount={setTippedAmount} setBottomView={props.setBottomView} close={props.close}/>
						</Elements>
					) : (
						<div>
							<img className={styles.coinJar} src={coinJar} alt="Coin Jar"/>
							<div className={styles.donatedHeader}>
								Thank you for your donation!
							</div>
							<div className={styles.donatedText}>
								Your donation
								of <span className={styles.donatedAmount}>${formatNumber(donatedAmount)}{tippedText}</span> has
								been successfully processed.  Thank you for
								supporting <span className={styles.donatedOrganization}>{props.organization?.name ? props.organization.name : 'the Giverist Foundation'}</span>.
                                A receipt has been sent to your e-mail address
							</div>
							<Button className={styles.donatedButton} text="Close" onClick={props.close}/>
						</div>
					)
				}
			</div>
		</div>
	)
};

export default DonateModal;